import React, { useState, useEffect } from "react";
import { Users, Search, Filter, Phone, X, Eye } from "lucide-react";
import {
  subscriberAPI,
  operatorAPI,
  feeCollectionAPI,
  formatCurrency,
  securitfeeAPI,
} from "../services/api";

const ManagementSubscriber = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterOperator, setFilterOperator] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriber, setSubscriber] = useState({});
  const [securityFees, setSecurityFees] = useState([]);
  const [totalSubscribersDefault, setTotalSubscribersDefault] = useState([]);
  const [filterTotalSubscribers, setFilterTotalSubscribers] = useState({
    total: 0,
    active: 0,
    suspended: 0,
  });
  const [isCollected, setIsCollected] = useState('all');

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    const fetchTotalSubscribers = async () => {
      const response = await subscriberAPI.getTotalSubscribers();
      console.log({ response });
      setTotalSubscribersDefault(response.data || []);
    };
    fetchTotalSubscribers();
  }, []);

  useEffect(() => {
    if (filterOperator === "all") {
      const total = totalSubscribersDefault?.reduce(
        (sum, subscriber) => sum + subscriber.total_subscribers,
        0,
      );
      const active = totalSubscribersDefault?.reduce(
        (sum, subscriber) => sum + subscriber.active_subscribers,
        0,
      );
      const suspended = total - active;
      console.log({ total, active, suspended });
      setFilterTotalSubscribers({
        total: total,
        active: active,
        suspended: suspended,
      });
    } else {
      const total = totalSubscribersDefault
        ?.filter(
          (subscriber) => subscriber.operator_id === parseInt(filterOperator),
        )
        .reduce((sum, subscriber) => sum + subscriber.total_subscribers, 0);
      const active = totalSubscribersDefault
        ?.filter(
          (subscriber) => subscriber.operator_id === parseInt(filterOperator),
        )
        .reduce((sum, subscriber) => sum + subscriber.active_subscribers, 0);
      const suspended = total - active;
      console.log({ total, active, suspended });
      setFilterTotalSubscribers({
        total: total,
        active: active,
        suspended: suspended,
      });
    }
  }, [filterOperator, totalSubscribersDefault]);

  // Fetch subscribers data
  const fetchSubscribers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: 50,
        search: searchTerm,
        operator: filterOperator,
        status: filterStatus,
        isCollected: isCollected,
      };

      const response = await subscriberAPI.getAll(params);
      if (response?.data) {
        setSubscribers(response.data.subscribers || []);
        setStatistics(response.data.statistics || {});
        setPagination(response.data.pagination || {});
      }
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      setError(
        "Failed to load subscribers. Please ensure the server is running and try refreshing the page.",
      );
      setSubscribers([]);
      setStatistics({});
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch operators for filter dropdown
  const fetchOperators = async () => {
    try {
      const response = await operatorAPI.getAll();
      setOperators(response.data || []);
    } catch (error) {
      console.error("Error fetching operators:", error);
    }
  };

  useEffect(() => {
    fetchOperators();
  }, []);

  useEffect(() => {
    fetchSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when pagination/filters change
  }, [currentPage, searchTerm, filterOperator, filterStatus, isCollected]);

  // Trigger search by MSISDN (called on button click or Enter)
  const handleSearchClick = () => {
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  // Clear MSISDN search and show all
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleOperatorFilter = (value) => {
    setFilterOperator(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  const handleViewDetail = async (sub) => {
    // console.log({subscriber});
    setSelectedSubscriber(sub);
    setShowDetailModal(true);
    setSubscriber({});
    setDetailError(null);
    setDetailLoading(true);
    try {
      const subscriber_id = sub?.subscriber_id ?? "";
      const msisdn = sub?.msisdn ?? "";
      const { data } = await subscriberAPI.getById(subscriber_id);
      const { data: securityFees } =
        await securitfeeAPI.getSecurityFeeTransactions(msisdn);
      setSecurityFees(securityFees ?? []);
      setSubscriber(data?.subscriber ?? {});
    } catch (err) {
      console.error("Error fetching subscriber transactions:", err);
      setDetailError(
        err?.message || "Failed to load security fee transactions.",
      );
      setSubscriber({});
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSubscriber(null);
    setSubscriber({});
    setSecurityFees([]);

    setDetailError(null);
  };

  // Status display: dot + label with consistent styling (case-insensitive)
  const getStatusDisplay = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active")
      return {
        label: "Active",
        dot: "bg-emerald-500",
        bg: "bg-emerald-50",
        text: "text-emerald-800",
        border: "border-emerald-200",
      };
    if (s === "suspended")
      return {
        label: "Suspended",
        dot: "bg-red-500",
        bg: "bg-red-50",
        text: "text-red-800",
        border: "border-red-200",
      };
    if (s === "pending")
      return {
        label: "Pending",
        dot: "bg-amber-500",
        bg: "bg-amber-50",
        text: "text-amber-800",
        border: "border-amber-200",
      };
    return {
      label: status ? String(status) : "Unknown",
      dot: "bg-gray-400",
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
    };
  };

  const formatBalance = (value) => {
    if (value == null || value === "") return "—";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString(undefined);
  };

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
    } catch {
      return "—";
    }
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="h-8 w-8 mr-3 text-blue-600" />
            Subscriber Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage and monitor subscriber accounts
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by MSISDN or phone number..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={handleSearchClick}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
              >
                <Search className="h-5 w-5 mr-2" />
                Search
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
                >
                  <X className="h-5 w-5 mr-2" />
                  Clear
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={filterOperator}
                  onChange={(e) => handleOperatorFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Operators</option>
                  {operators.map((operator) => (
                    <option
                      key={operator.operator_id}
                      value={operator.operator_id}
                    >
                      {operator.operator_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={isCollected}
                  onChange={(e) => setIsCollected(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All</option>
                  <option value="true">Collected</option>
                  <option value="false">Not Collected</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={filterStatus}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPEND">Suspended</option>
                  <option value="DEACTIVE">Deactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => {
                    setError(null);
                    fetchSubscribers();
                  }}
                  className="text-red-700 hover:text-red-900 text-sm underline"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Subscribers
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {filterTotalSubscribers.total.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filterTotalSubscribers.active.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Suspended</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filterTotalSubscribers.suspended.toLocaleString() || "0"}
                </p>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">New This Month</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.new_this_month?.toLocaleString() || '0'}</p>
            </div>
          </div>
        </div> */}
        </div>

        {/* Subscribers Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Subscriber List
            </h3>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">Loading subscribers...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscriber
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Operator
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last charge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscribers.map((subscriber) => (
                    <tr
                      key={subscriber.subscriber_id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <Phone className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {subscriber.msisdn}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone className="h-4 w-4 mr-1" />
                              {subscriber.email || "No phone"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {
                            operators.find(
                              (o) => o.operator_id === subscriber.operator_id,
                            )?.operator_name
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {subscriber.servicetype || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const s = getStatusDisplay(subscriber.status);
                          return (
                            <span
                              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs font-medium ${s.bg} ${s.text} ${s.border}`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full shrink-0 ${s.dot}`}
                                aria-hidden
                              />
                              {s.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                        {formatBalance(subscriber.balance)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(
                          subscriber.lastchargedate ??
                            subscriber.lastChargeDate,
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {subscriber.lastactivity
                          ? formatDate(subscriber.lastactivity)
                          : "—"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleViewDetail(subscriber)}
                          className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-800 font-medium"
                        >
                          <Eye className="h-4 w-4" />
                          View detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {subscribers.length === 0 && !loading && (
            <div className="p-6 text-center text-gray-500">
              No subscribers found matching your criteria.
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage(
                    Math.min(currentPage + 1, pagination.total_pages),
                  )
                }
                disabled={currentPage === pagination.total_pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * pagination.per_page + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(
                      currentPage * pagination.per_page,
                      pagination.total,
                    )}
                  </span>{" "}
                  of <span className="font-medium">{pagination.total}</span>{" "}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from(
                    { length: Math.min(5, pagination.total_pages) },
                    (_, i) => {
                      const page = i + Math.max(1, currentPage - 2);
                      if (page > pagination.total_pages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    },
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(currentPage + 1, pagination.total_pages),
                      )
                    }
                    disabled={currentPage === pagination.total_pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Security fee transactions detail modal */}
        {showDetailModal && selectedSubscriber && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={closeDetailModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="detail-modal-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h2
                  id="detail-modal-title"
                  className="text-lg font-semibold text-gray-900"
                >
                  Security fee transactions – {selectedSubscriber.msisdn}
                </h2>
                <button
                  type="button"
                  onClick={closeDetailModal}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* <pre>{JSON.stringify(selectedSubscriber, null, 2)}</pre> */}
              <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50 text-sm text-gray-600 shrink-0">
                {selectedSubscriber.operator_id && (
                  <span>
                    Operator:{" "}
                    {
                      operators.find(
                        (o) => o.operator_id === selectedSubscriber.operator_id,
                      )?.operator_name
                    }
                  </span>
                )}
                {selectedSubscriber.servicetype && (
                  <span className="ml-4">
                    Service: {selectedSubscriber.servicetype}
                  </span>
                )}
                {selectedSubscriber.status && (
                  <span className="ml-4">
                    Status: {selectedSubscriber.status}
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {detailLoading && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-500 border-t-transparent" />
                    <p className="mt-3 text-gray-600">Loading transactions…</p>
                  </div>
                )}
                {detailError && !detailLoading && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                    {detailError}
                  </div>
                )}
                {!detailLoading && !detailError && subscriber.length === 0 && (
                  <p className="text-gray-500 text-center py-8">
                    No security fee transactions found for this MSISDN.
                  </p>
                )}
                {!detailLoading &&
                  !detailError &&
                  subscriber &&
                  Object.keys(subscriber).length > 0 && (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      {[
                        { key: "msisdn", label: "MSISDN" },
                        { key: "imsi", label: "IMSI" },
                        { key: "subscriber_type", label: "Subscriber type" },
                        {
                          key: "registration_date",
                          label: "Registration date",
                          format: "date",
                        },
                        { key: "status", label: "Status" },
                        { key: "status_detail", label: "Status detail" },
                        { key: "balance", label: "Balance", format: "balance" },
                        {
                          key: "charge_fee",
                          label: "Charge fee",
                          format: "balance",
                        },
                        {
                          key: "last_charge_date",
                          label: "Last charge date",
                          format: "date",
                        },
                        {
                          key: "last_activity_date",
                          label: "Last activity date",
                          format: "date",
                        },
                        {
                          key: "created_at",
                          label: "Created at",
                          format: "date",
                        },
                        {
                          key: "updated_at",
                          label: "Updated at",
                          format: "date",
                        },
                      ].map(({ key, label, format }) => {
                        const raw = subscriber[key];
                        const value =
                          raw == null || raw === ""
                            ? "—"
                            : format === "date"
                              ? formatDate(raw)
                              : format === "balance"
                                ? formatBalance(raw)
                                : String(raw);
                        return (
                          <div key={key} className="flex flex-col">
                            <dt className="text-gray-500 font-medium">
                              {label}
                            </dt>
                            <dd className="text-gray-900 mt-0.5">{value}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  )}

                {/* <pre>{JSON.stringify(securityFees, null, 2)}</pre> */}

                <br />
                {!detailLoading && !detailError && securityFees.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Transaction Ref
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Period
                          </th>
                          {/* <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th> */}
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {securityFees.map((tx, index) => {
                          const date =
                            tx.date ??
                            tx.collection_date ??
                            tx.collection_date ??
                            tx.collection_date ??
                            "—";
                          const period =
                            tx.period ??
                            tx.billing_period ??
                            tx.month ??
                            tx.year ??
                            "—";
                          // const type = tx.type ?? tx.description ?? tx.transaction_type ?? tx.service_type ?? '—';
                          const amount =
                            tx.amount ??
                            tx.fee_amount ??
                            tx.feeAmount ??
                            tx.amount_kip ??
                            0;
                          const status =
                            tx.status ?? tx.transaction_status ?? "—";
                          const transaction_ref =
                            tx.transaction_ref ??
                            tx.transaction_ref ??
                            tx.transaction_ref ??
                            tx.transaction_ref ??
                            "—";
                          return (
                            <tr
                              key={tx.id ?? tx.transaction_id ?? index}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                                {formatDate(date)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                                {String(transaction_ref)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                                {String(period)}
                              </td>
                              {/* <td className="px-4 py-3 whitespace-nowrap text-gray-900">{String(type)}</td> */}
                              <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-900">
                                {formatCurrency(amount)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                                {String(status)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ManagementSubscriber;
